import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL || '';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuth = async () => {
      const token = searchParams.get('token');
      const isNewUser = searchParams.get('new') === 'true';
      const errorMessage = searchParams.get('error');

      if (errorMessage) {
        setError(decodeURIComponent(errorMessage));
        return;
      }

      if (token) {
        // Store access token
        localStorage.setItem('accessToken', token);

        // Fetch user data and store it for AuthContext to pick up
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            localStorage.setItem('user', JSON.stringify(data.user));
          }
        } catch (err) {
          console.error('Failed to fetch user data:', err);
          // Continue anyway - the token is valid, user will be fetched on next mount
        }

        // Redirect based on new user status
        if (isNewUser) {
          navigate('/welcome', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        setError('No authentication token received');
      }
    };

    handleAuth();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="max-w-md p-8 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/50 text-center">
          <div className="text-red-500 dark:text-red-400 text-5xl mb-4">!</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Authentication Failed
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-slate-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto" />
        <p className="mt-4 text-gray-600 dark:text-slate-400">Completing sign in...</p>
      </div>
    </div>
  );
}
